import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { LayoutDashboard, LogOut, ArrowLeft, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type SubmissionDetail = {
  id: string;
  created_at: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  company: string | null;
  topic: string;
  message: string;
  consent: boolean;
  source_url: string | null;
  user_agent: string | null;
  status: string | null;
};

const AdminSubmissionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { signOut, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (!id) return;
      const { data, error } = await supabase
        .from("contact_submissions")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error || !data) {
        toast({ title: "Error", description: "Submission not found.", variant: "destructive" });
      } else {
        setSubmission(data);
      }
      setLoading(false);
    };
    fetch();
  }, [id]);

  const updateStatus = async (newStatus: string) => {
    if (!id) return;
    setUpdating(true);
    const { error } = await supabase.from("contact_submissions").update({ status: newStatus }).eq("id", id);
    if (error) {
      toast({ title: "Error", description: "Failed to update status.", variant: "destructive" });
    } else {
      setSubmission((prev) => prev ? { ...prev, status: newStatus } : prev);
      toast({ title: "Updated", description: `Status changed to "${newStatus}".` });
    }
    setUpdating(false);
  };

  const Field = ({ label, value }: { label: string; value: string | null | boolean }) => (
    <div className="py-3 border-b last:border-b-0">
      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm">{value === null || value === "" ? "—" : String(value)}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-bold">Admin Portal</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={signOut}><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        <Link to="/admin/submissions">
          <Button variant="ghost" size="sm" className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" /> Back to list</Button>
        </Link>

        {loading ? (
          <p className="text-muted-foreground py-8 text-center">Loading...</p>
        ) : !submission ? (
          <p className="text-muted-foreground py-8 text-center">Submission not found.</p>
        ) : (
          <div className="bg-card border rounded-lg p-6 space-y-0">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Submission Detail</h2>
              <Badge variant={submission.status === "handled" ? "secondary" : "default"}>
                {submission.status || "new"}
              </Badge>
            </div>

            <Field label="Date" value={submission.created_at ? new Date(submission.created_at).toLocaleString() : null} />
            <Field label="Full Name" value={submission.full_name} />
            <Field label="Email" value={submission.email} />
            <Field label="Phone" value={submission.phone} />
            <Field label="Company" value={submission.company} />
            <Field label="Topic" value={submission.topic} />
            <Field label="Message" value={submission.message} />
            <Field label="Consent" value={submission.consent ? "Yes" : "No"} />
            <Field label="Source URL" value={submission.source_url} />
            <Field label="User Agent" value={submission.user_agent} />

            <div className="pt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Update Status:</span>
                <Select value={submission.status || "new"} onValueChange={updateStatus} disabled={updating}>
                  <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="handled">Handled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete submission?</AlertDialogTitle>
                    <AlertDialogDescription>This will permanently remove this submission. This action cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={async () => {
                        const { error } = await supabase.from("contact_submissions").delete().eq("id", id!);
                        if (error) {
                          toast({ title: "Error", description: "Failed to delete.", variant: "destructive" });
                        } else {
                          toast({ title: "Deleted", description: "Submission removed." });
                          navigate("/admin/submissions");
                        }
                      }}
                    >Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSubmissionDetail;
